/**
 * @file DashboardUtils.js
 * @description Utility functions, constants, mock data, and helper methods for the main DashboardPage.js component and its related cards.
 */

import { subDays } from 'date-fns';
import JSZip from 'jszip';
import {
    DashboardTypeEnum,
    LogEventType,
    tenantTierLabels,
} from '../../utils/data/enums';
import {
    buildCsvContent,
    buildCsvReportData,
    exportCsvReport,
} from '../../utils/reporting/csvReportExporter';
import { getText } from '../../i18n';

export const buildDashboardLoadingState = (dashboardTypes, isLoading) => {
    return dashboardTypes.reduce((nextState, dashboardTypeId) => {
        nextState[dashboardTypeId] = isLoading;
        return nextState;
    }, {});
};

export const mapDashboardResponseToCardData = (
    dashboardTypes,
    responseFieldByType,
    allDashboardResponse,
    defaultDataByType,
) => {
    return dashboardTypes.reduce((nextDashboardData, dashboardTypeId) => {
        const responseField = responseFieldByType[dashboardTypeId];
        const dashboardTypeData = allDashboardResponse?.[responseField];
        if (dashboardTypeData === null || dashboardTypeData === undefined) {
            nextDashboardData[dashboardTypeId] =
                defaultDataByType[dashboardTypeId];
            return nextDashboardData;
        }
        nextDashboardData[dashboardTypeId] = dashboardTypeData;
        return nextDashboardData;
    }, {});
};

/* Constants used for the overall Dashboard Page */

/**
 * Default start date time for the Dashboard page, which is 30 days before the current date.
 * @example Wed Dec 10 2025 17:27:02 GMT-0500 (Eastern Standard Time)
 */
export const DASHBOARD_DEFAULT_START_DATE = subDays(new Date(), 30);

/**
 * Default end date time for the Dashboard page, which is the current date in the user's timezone.
 * @example Wed Jan 09 2026 17:28:08 GMT-0500 (Eastern Standard Time)
 */
export const DASHBOARD_DEFAULT_END_DATE = new Date();

/**
 * Default width in px for the spinner used for the loading of dashboards.
 */
export const DASHBOARD_DEFAULT_SPINNER_WIDTH = 50;
/**
 * Maximum number of population groups to display before adding the "Show More" ability to the user.
 * @example "Group 1, Group 2 ... + 1 more"
 */
export const DASHBOARD_POPULATION_GROUPS_MAX = 5;

/**
 * Array of strings for the names of the default population groups for the Dashboard.
 */
export const DASHBOARD_DEFAULT_POPULATION_GROUP_NAMES = [
    'Status-Active',
    'Status-Leave of Absence',
    'Status-Terminated',
];

/**
 * Date format of the header to display the selected end date.
 *
 * This is used in (1) Registration, (2) Employment Status, and (3) Messages - Preferred Destination dashboards.
 * @example "As of Aug 3 2025"
 */
export const DASHBOARD_AS_OF_DATE_FORMAT = 'MMM d yyyy';

/* Constants used for the Messages Sent dashboard */

/**
 * Date format of API response for the data used in the Messages Sent dashboard.
 * @example "2025-01-15"
 */
export const DASHBOARD_MESSAGES_SENT_DATE_FORMAT_DATA = 'yyyy-MM-dd';

/**
 * Date format of the X axis for Messages Sent dashboard.
 * @example "Jan 1"
 */

export const DASHBOARD_MESSAGES_SENT_DATE_FORMAT_X_AXIS = 'MMM d';
/**
 * Date format of the tooltip for the Messages Sent dashboard.
 * @example "Jan 14, 2024"
 */

export const DASHBOARD_MESSAGES_SENT_DATE_FORMAT_TOOLTIP =
    getText('msg_d3ad64c558c9');
/**
 * Maximum number of X axis labels to display for the Messages Sent dashboard. This is used to evenly space out the labels.
 * @example "Jan 1, Mar 1, May 15, Jul 31, Dec 31"
 */
export const DASHBOARD_MESSAGES_SENT_MAX_X_AXIS_LABELS = 6;

/* Constants used for the Most Clicked Content dashboard */

/**
 * Maximum number of items to display for the Most Clicked Content dashboard.
 */
export const DASHBOARD_MOST_CLICKED_CONTENT_MAX_ITEMS = 5;
/**
 * Bar sizing to use for the Most Clicked Content dashboard.
 */
export const DASHBOARD_MOST_CLICKED_CONTENT_BAR_SIZING = 44;

/* Constants used for the Most Visited Pages dashboard */

/**
 * Maximum number of items to display for the Most Visited Pages dashboard.
 */
export const DASHBOARD_MOST_VISITED_PAGES_MAX_ITEMS = 8;

/**
 * Maximum number of characters to display on the Y axis label before text cutoff for the Most Visited Pages dashboard.
 * @example "A long title..."
 */
export const DASHBOARD_MOST_VISITED_PAGES_Y_AXIS_LABEL_CHARACTERS_MAX = 12;

/**
 * Bar height to use for the Most Visited Pages dashboard.
 */
export const DASHBOARD_MOST_VISITED_PAGES_BAR_HEIGHT = 25;

/**
 * Sections for the SideDrawer, when the user clicks "View Report Definitions"
 * @example [ { label: 'Session' text: 'A session is a visit to your site. }, ... ]
 */
export const DASHBOARD_REPORT_DEFINITIONS_SECTIONS = [
    {
        label: getText('msg_f7f1997c6cd1'),
        text: getText('msg_b4513b50afaa'),
    },
    {
        label: getText('msg_bdfb14bd555c'),
        text: getText('msg_e2817fb8ea8b'),
    },
    {
        label: getText('msg_8acbd196817f'),
        text: getText('msg_378eb365d6a4'),
    },
    {
        label: getText('msg_b2c0e6762ad4'),
        text: getText('msg_ce3a0d5681aa'),
    },
    {
        label: getText('msg_8f7c750003a1'),
        text: getText('msg_b5080f3bff64'),
    },
    {
        label: getText('msg_ca710e818684'),
        text: getText('msg_6f3896568195'),
    },
    {
        label: getText('msg_3a6d445511d3'),
        text: getText('msg_aa5788fee454'),
    },
];

export const DASHBOARD_MOST_VISITED_PAGES_DEFINITION_SECTIONS = [
    {
        label: getText('msg_709a23220f2c'),
        text: getText('msg_1856b6869a1b'),
    },
    {
        label: getText('msg_24be61285e09'),
        text: getText('msg_0413ba32abe4'),
    },
    {
        label: getText('msg_2c483699fa6d'),
        text: getText('msg_cfb96ae6fa1a'),
    },
    {
        label: getText('msg_a4fb03092fc3'),
        text: getText('msg_f2f3157f89a1'),
    },
];

export const DASHBOARD_MOST_CLICKED_CONTENT_DEFINITION_SECTIONS = [
    {
        label: getText('msg_a9535fa5fef5'),
        text: getText('msg_da61160f6eef'),
    },
    {
        label: getText('msg_3deb74565196'),
        text: getText('msg_638e28bca457'),
    },
    {
        label: getText('msg_5b7f4b8e2a11'),
        text: getText('msg_2e8c4c8ad913'),
    },
    {
        label: getText('msg_8b97ba460c72'),
        text: getText('msg_519694ed7884'),
    },
    {
        label: getText('msg_847e7f99d638'),
        text: getText('msg_7f72556ce237'),
    },
];

/**
 * Sections for the SideDrawer on the Multi Tenant Dashboard, when the user clicks "View Report Definitions"
 * @example [ { label: 'Session' text: 'A session is a visit to your site. }, ... ]
 */
export const MULTITENANT_DASHBOARD_REPORT_DEFINITIONS_SECTIONS = [
    {
        label: getText('msg_f7f1997c6cd1'),
        text: getText('msg_7e5c85afa65d'),
    },
    {
        label: getText('msg_bdfb14bd555c'),
        text: getText('msg_5f957411d289'),
    },
    {
        label: getText('msg_8acbd196817f'),
        text: getText('msg_9805ed691437'),
    },
    {
        label: getText('msg_b2c0e6762ad4'),
        text: getText('msg_ce3a0d5681aa'),
    },
    {
        label: getText('msg_5cf89afb8bc8'),
        text: getText('msg_8896db00e674'),
    },
    {
        label: getText('msg_56638b47b794'),
        text: getText('msg_1bd8797cdfc3'),
    },
    {
        label: getText('msg_9ad812f73e02'),
        text: getText('msg_696f5f218c73'),
    },
];

export function getDrillDownDefinitionHeader(dashboardTypeId) {
    if (dashboardTypeId === DashboardTypeEnum.MostVisitedPages) {
        return 'Most Visited Pages';
    }

    if (dashboardTypeId === DashboardTypeEnum.MostClickedContent) {
        return 'Most Clicked Content';
    }

    return 'Terms and Definitions';
}

export function getDrillDownDefinitionSections(dashboardTypeId) {
    if (dashboardTypeId === DashboardTypeEnum.MostVisitedPages) {
        return DASHBOARD_MOST_VISITED_PAGES_DEFINITION_SECTIONS;
    }

    if (dashboardTypeId === DashboardTypeEnum.MostClickedContent) {
        return DASHBOARD_MOST_CLICKED_CONTENT_DEFINITION_SECTIONS;
    }

    return DASHBOARD_REPORT_DEFINITIONS_SECTIONS;
}

/**
 * Tooltip message text for the individual Dashboards.
 * @example [DashboardTypeEnum.Registration]: 'Displays metrics related to user registrations...' ]
 */
export const DASHBOARD_TOOLTIP_MESSAGE_TEXT = {
    [DashboardTypeEnum.Registration]: `Displays metrics related to user registrations. This will also include spouses and dependents if you have enabled the "Additional Users" feature.`,
    [DashboardTypeEnum.EmploymentStatus]: `Includes any individual with an Employment Status on the individual record. This number can be different than the population number on registration since it also includes Terminated employees. The total number will not include test records.`,
    [DashboardTypeEnum.SessionsByDevice]: `This chart shows the number of user sessions broken down by device type (Desktop, Apple, Android). Use this data to understand how employees access the platform and tailor your communication methods for better engagement.`,
    [DashboardTypeEnum.SessionsDaily]: `This chart shows the average number of sessions per day. Use this data to identify peak engagement days and determine the best times to communicate or post new content.`,
    [DashboardTypeEnum.MostVisitedPages]: `Each time a page is viewed, it is recorded as a separate visit. For example, if an employee clicks to view an "Employee Resources" page three times, it will be counted as three visits.`,
    [DashboardTypeEnum.MostClickedContent]: `Each time a user interacts with content, it is recorded as a separate click. For example, if an employee clicks to view the same video twice, it will be counted as two clicks.`,
    [DashboardTypeEnum.MessagesUserPreference]: `The preferred communication method (text, email or push message) of all registered users as of today's date.`,
    [DashboardTypeEnum.MessagesSent]: `This graph shows the number of messages that were created and sent out within the selected timeframe. This chart does not display the number of users that received the message.`,
    [DashboardTypeEnum.ClientStatus]: `Shows number of clients in each status category: In Progress, Live, Demo, Inactive - found on the client list screen`,
    [DashboardTypeEnum.ClientTier]: `Displays distribution of selected clients’ populations across medal levels: Base, Bronze, Silver, Gold.`,
};

/**
 * Initial loading state for the individual Dashboards.
 * @example [DashboardTypeEnum.Registration]: false ], ...
 */
export const DASHBOARD_DEFAULT_LOADING_STATE = {
    [DashboardTypeEnum.Registration]: false,
    [DashboardTypeEnum.EmploymentStatus]: false,
    [DashboardTypeEnum.SessionsByDevice]: false,
    [DashboardTypeEnum.SessionsDaily]: false,
    [DashboardTypeEnum.MostVisitedPages]: false,
    [DashboardTypeEnum.MostClickedContent]: false,
    [DashboardTypeEnum.MessagesUserPreference]: false,
    [DashboardTypeEnum.MessagesSent]: false,
};

/**
 * Empty set of Dashboard data which is the default response from the API when there is no data found for a given set of population groups.
 *
 * The following Dashboards return data with counts of 0 for this case:
 * - Registration
 * - Employment Status
 * - Sessions By Device
 * - Sessions - Daily
 * - Messages - Preferred Destination
 *
 * The following Dashboards return empty arrays as a default when there is no data available for the given parameters.
 * - Messages Sent
 * - Most Visited Pages
 * - Most Clicked Content
 */
export const DASHBOARD_DEFAULT_DATA = {
    [DashboardTypeEnum.Registration]: {
        populationTotal: 0,
        registeredUsers: 0,
        activeUsers: 0,
        newUsers: 0,
    },
    [DashboardTypeEnum.EmploymentStatus]: [
        { count: 0, employmentStatus: 'Active' },
        { count: 0, employmentStatus: 'Terminated' },
        { count: 0, employmentStatus: 'LeaveOfAbsence' },
    ],
    [DashboardTypeEnum.SessionsByDevice]: [],
    [DashboardTypeEnum.SessionsDaily]: [
        {
            dayOfWeek: 'Monday',
            count: 0,
            dayOfWeekEnum: 1,
        },
        {
            dayOfWeek: 'Tuesday',
            count: 0,
            dayOfWeekEnum: 2,
        },
        {
            dayOfWeek: 'Wednesday',
            count: 0,
            dayOfWeekEnum: 3,
        },
        {
            dayOfWeek: 'Thursday',
            count: 0,
            dayOfWeekEnum: 4,
        },
        {
            dayOfWeek: 'Friday',
            count: 0,
            dayOfWeekEnum: 5,
        },
        {
            dayOfWeek: 'Saturday',
            count: 0,
            dayOfWeekEnum: 6,
        },
        {
            dayOfWeek: 'Sunday',
            count: 0,
            dayOfWeekEnum: 0,
        },
    ],
    [DashboardTypeEnum.MostVisitedPages]: [],
    [DashboardTypeEnum.MostClickedContent]: [],
    [DashboardTypeEnum.MessagesUserPreference]: [
        {
            notificationPreferredMethodId: 0,
            notificationPreferredMethodName: 'None',
            count: 0,
        },
        {
            notificationPreferredMethodId: 1,
            notificationPreferredMethodName: 'IndividualEmail',
            count: 0,
        },
        {
            notificationPreferredMethodId: 2,
            notificationPreferredMethodName: 'IndividualSMS',
            count: 0,
        },
        {
            notificationPreferredMethodId: 3,
            notificationPreferredMethodName: 'PushNotification',
            count: 0,
        },
        {
            notificationPreferredMethodId: 4,
            notificationPreferredMethodName: 'UserEmail',
            count: 0,
        },
        {
            notificationPreferredMethodId: 5,
            notificationPreferredMethodName: 'UserSMS',
            count: 0,
        },
    ],
    [DashboardTypeEnum.MessagesSent]: [],
};

const DASHBOARD_EXPORT_SELECTOR = '[data-dashboard-export-root="true"]';
const DASHBOARD_PDF_MARGIN_MM = 10;
const DASHBOARD_PDF_SECTION_GAP_MM = 6;
const DASHBOARD_PDF_HEADER_SECTION_GAP_MM = 2;
const DASHBOARD_EXPORT_FIXED_WIDTH_PX = 1195;
const DASHBOARD_EXPORT_FIXED_PADDING_LEFT_PX = 60;
const DASHBOARD_EXPORT_DEFAULT_CONFIG = {
    rootSelector: DASHBOARD_EXPORT_SELECTOR,
    filenameBuilder: getDashboardPdfFilename,
    headerContainerSelector: '.dashboard-header-container',
    cardRowSelector: '.dashboard-card-container',
    cardColumnsPerRow: 2,
    sectionGapMm: DASHBOARD_PDF_SECTION_GAP_MM,
    headerSectionGapMm: DASHBOARD_PDF_HEADER_SECTION_GAP_MM,
    marginMm: DASHBOARD_PDF_MARGIN_MM,
    fixedWidthPx: DASHBOARD_EXPORT_FIXED_WIDTH_PX,
    fixedPaddingLeftPx: DASHBOARD_EXPORT_FIXED_PADDING_LEFT_PX,
    useFixedLayout: true,
    ignoredSelectors: [
        '.dashboard-card-actions',
        '.dashboard-card-tooltip-icon-wrapper',
    ],
    headerIgnoredSelectors: ['.view-report-definitions'],
    headerIgnoredButtonClassNames: [
        'ing-button-primary-left',
        'align-self-baseline',
    ],
};

function getExportElement(rootSelector) {
    return document.querySelector(rootSelector);
}

function getDashboardPdfFilename() {
    return `dashboardReport${getDateStamp()}.pdf`;
}

function getDateStamp() {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    const year = now.getFullYear();

    return `${year}-${month}-${day}`;
}

function waitForNextPaintCycle() {
    return new Promise(resolve => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(resolve);
        });
    });
}

function getDashboardRowsFromPayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (payload && typeof payload === 'object') {
        return [payload];
    }

    return [];
}

function getDashboardTypeName(dashboardTypeId) {
    const dashboardTypeName = Object.keys(DashboardTypeEnum).find(
        key => DashboardTypeEnum[key] === dashboardTypeId,
    );

    return dashboardTypeName || 'Dashboard';
}

function getDashboardCsvFilename(dashboardTypeId) {
    const dashboardTypeName = getDashboardTypeName(dashboardTypeId)
        .replace(/[^a-zA-Z0-9]+/g, '')
        .replace(/\s+/g, '');

    return `${dashboardTypeName}Report${getDateStamp()}.csv`;
}

function getDashboardCsvZipFilename() {
    return `dashboardReports${getDateStamp()}.zip`;
}

const CLIENT_STATUS_LABEL_BY_STATUS = {
    1: 'Active',
    2: 'Demo + Test',
    3: 'Demo + Test',
    4: 'Inactive',
    5: 'Demo + Test',
    6: 'In Progress',
    7: 'Inactive',
    8: 'In Progress',
    9: 'In Progress',
};

const CLIENT_STATUS_DISPLAY_ORDER = [
    'In Progress',
    'Active',
    'Inactive',
    'Demo + Test',
];

function getClientStatusCsvRows(payload) {
    const rows = getDashboardRowsFromPayload(payload);
    const countsByStatus = {
        'In Progress': 0,
        Active: 0,
        Inactive: 0,
        'Demo + Test': 0,
    };

    rows.forEach(row => {
        const statusLabel =
            CLIENT_STATUS_LABEL_BY_STATUS[row?.status] || 'In Progress';
        countsByStatus[statusLabel] += Number(row?.count) || 0;
    });

    return CLIENT_STATUS_DISPLAY_ORDER.map(status => ({
        count: countsByStatus[status],
        status,
    }));
}

function getDashboardCsvRowsSelector(dashboardTypeId) {
    if (dashboardTypeId === DashboardTypeEnum.ClientStatus) {
        return getClientStatusCsvRows;
    }

    return getDashboardRowsFromPayload;
}

function getDashboardCsvRowMapper(dashboardTypeId) {
    if (dashboardTypeId === DashboardTypeEnum.ClientTier) {
        return row => ({
            count: Number(row?.count) || 0,
            tier: tenantTierLabels[row?.tier] || 'Unknown',
        });
    }

    return undefined;
}

function getDashboardCsvReportDefinition(dashboardTypeId, payload) {
    return {
        getData: async () => payload,
        filenamePrefix: 'dashboard',
        filenameBuilder: () => getDashboardCsvFilename(dashboardTypeId),
        selectRows: getDashboardCsvRowsSelector(dashboardTypeId),
        mapRow: getDashboardCsvRowMapper(dashboardTypeId),
    };
}

function downloadBlobFile(blob, filename) {
    const link = document.createElement('a');
    if (link.download === undefined) {
        return;
    }

    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export async function exportDashboardCardCsv(dashboardTypeId, payload) {
    const reportDefinition = getDashboardCsvReportDefinition(
        dashboardTypeId,
        payload,
    );
    await exportCsvReport(reportDefinition);
}

export async function exportDashboardAllCardsCsvZip(
    dashboardDataByType,
    dashboardTypeIds = [
        DashboardTypeEnum.Registration,
        DashboardTypeEnum.EmploymentStatus,
        DashboardTypeEnum.SessionsByDevice,
        DashboardTypeEnum.SessionsDaily,
        DashboardTypeEnum.MostVisitedPages,
        DashboardTypeEnum.MostClickedContent,
        DashboardTypeEnum.MessagesUserPreference,
        DashboardTypeEnum.MessagesSent,
    ],
) {
    const zip = new JSZip();

    for (const dashboardTypeId of dashboardTypeIds) {
        const reportDefinition = getDashboardCsvReportDefinition(
            dashboardTypeId,
            dashboardDataByType[dashboardTypeId],
        );
        const reportData = await buildCsvReportData(reportDefinition);
        const csvContent = buildCsvContent(
            reportData.rows,
            reportData.headers,
            reportData.addTextQualifier,
        );

        zip.file(reportData.filename, csvContent);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    downloadBlobFile(zipBlob, getDashboardCsvZipFilename());
}

function waitForDashboardRenderCompletion() {
    return new Promise(resolve => {
        window.setTimeout(resolve, 300);
    });
}

function getDashboardExportSections(
    dashboardElement,
    headerContainerSelector,
    cardRowSelector,
) {
    const headerElement = dashboardElement.querySelector(
        headerContainerSelector,
    );
    const headerSections = headerElement
        ? Array.from(headerElement.children).filter(Boolean)
        : [];
    const dashboardRows = Array.from(
        dashboardElement.querySelectorAll(cardRowSelector),
    );
    const dashboardCards = dashboardRows.flatMap(rowElement =>
        Array.from(rowElement.children),
    );

    return {
        headerSections,
        dashboardCards,
    };
}

function getCaptureDimensions(element) {
    const width = Math.max(element.scrollWidth, element.clientWidth);
    const height = Math.max(element.scrollHeight, element.clientHeight);

    return { width, height };
}

async function captureSectionCanvas(
    html2canvas,
    sectionElement,
    shouldIgnoreElement,
) {
    const { width, height } = getCaptureDimensions(sectionElement);

    return html2canvas(sectionElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        windowWidth: width,
        windowHeight: height,
        ignoreElements: shouldIgnoreElement,
    });
}

function createIgnoreElementsPredicate(exportConfig) {
    return element => {
        if (!element?.closest) {
            return false;
        }

        const isInIgnoredSelector = exportConfig.ignoredSelectors.some(
            selector => element.closest(selector),
        );
        if (isInIgnoredSelector) {
            return true;
        }

        const isInHeader = element.closest(
            exportConfig.headerContainerSelector,
        );
        if (!isInHeader) {
            return false;
        }

        const isInIgnoredHeaderSelector =
            exportConfig.headerIgnoredSelectors.some(selector =>
                element.closest(selector),
            );
        if (isInIgnoredHeaderSelector) {
            return true;
        }

        if (element.tagName !== 'BUTTON') {
            return false;
        }

        return exportConfig.headerIgnoredButtonClassNames.every(className =>
            element.classList.contains(className),
        );
    };
}

function applyFixedDashboardExportLayout(
    dashboardElement,
    fixedWidthPx,
    fixedPaddingLeftPx,
) {
    const previousStyles = {
        width: dashboardElement.style.width,
        maxWidth: dashboardElement.style.maxWidth,
        margin: dashboardElement.style.margin,
        marginLeft: dashboardElement.style.marginLeft,
        marginRight: dashboardElement.style.marginRight,
        paddingLeft: dashboardElement.style.paddingLeft,
    };

    dashboardElement.style.width = `${fixedWidthPx}px`;
    dashboardElement.style.maxWidth = `${fixedWidthPx}px`;
    dashboardElement.style.margin = '0';
    dashboardElement.style.marginLeft = '0';
    dashboardElement.style.marginRight = '0';
    dashboardElement.style.paddingLeft = `${fixedPaddingLeftPx}px`;

    return () => {
        dashboardElement.style.width = previousStyles.width;
        dashboardElement.style.maxWidth = previousStyles.maxWidth;
        dashboardElement.style.margin = previousStyles.margin;
        dashboardElement.style.marginLeft = previousStyles.marginLeft;
        dashboardElement.style.marginRight = previousStyles.marginRight;
        dashboardElement.style.paddingLeft = previousStyles.paddingLeft;
    };
}

async function loadPdfLibrary() {
    const jsPdfModule = await import('jspdf');

    if (jsPdfModule.jsPDF) {
        return jsPdfModule.jsPDF;
    }

    if (jsPdfModule.default?.jsPDF) {
        return jsPdfModule.default.jsPDF;
    }

    return jsPdfModule.default;
}

/**
 * Helper method used to export the Dashboard page as a PDF.
 * @returns A PDF file snapshot of the Dashboard page including all dashboard containers.
 */
export const exportDashboardReportPdf = async () => {
    await exportDashboardReportPdfWithConfig();
};

export async function exportDashboardReportPdfWithConfig(config = {}) {
    const exportConfig = {
        ...DASHBOARD_EXPORT_DEFAULT_CONFIG,
        ...config,
    };

    const dashboardElement = getExportElement(exportConfig.rootSelector);
    if (!dashboardElement) {
        throw new Error(
            `Dashboard export element was not found for selector: ${exportConfig.rootSelector}`,
        );
    }

    const restoreExportLayout = exportConfig.useFixedLayout
        ? applyFixedDashboardExportLayout(
              dashboardElement,
              exportConfig.fixedWidthPx,
              exportConfig.fixedPaddingLeftPx,
          )
        : () => {};

    try {
        await waitForNextPaintCycle();
        await waitForDashboardRenderCompletion();
        const [{ default: html2canvas }, jsPDF] = await Promise.all([
            import('html2canvas'),
            loadPdfLibrary(),
        ]);
        const pdfDocument = new jsPDF({
            orientation: 'p',
            unit: 'mm',
            format: 'a4',
        });

        const pageWidth = pdfDocument.internal.pageSize.getWidth();
        const pageHeight = pdfDocument.internal.pageSize.getHeight();
        const contentWidth = pageWidth - exportConfig.marginMm * 2;
        const pageContentHeight = pageHeight - exportConfig.marginMm * 2;
        let currentY = exportConfig.marginMm;

        const shouldIgnoreElement = createIgnoreElementsPredicate(exportConfig);

        const renderFullWidthSection = (
            sectionImageData,
            sectionHeight,
            sectionGap = exportConfig.sectionGapMm,
        ) => {
            if (sectionHeight > pageContentHeight) {
                if (currentY !== exportConfig.marginMm) {
                    pdfDocument.addPage();
                    currentY = exportConfig.marginMm;
                }

                let remainingHeight = sectionHeight;
                let offsetHeight = 0;
                while (remainingHeight > 0) {
                    pdfDocument.addImage(
                        sectionImageData,
                        'PNG',
                        exportConfig.marginMm,
                        exportConfig.marginMm - offsetHeight,
                        contentWidth,
                        sectionHeight,
                        undefined,
                        'FAST',
                    );

                    remainingHeight -= pageContentHeight;
                    offsetHeight += pageContentHeight;

                    if (remainingHeight > 0) {
                        pdfDocument.addPage();
                    }
                }

                currentY = exportConfig.marginMm;
                return;
            }

            if (currentY + sectionHeight > pageHeight - exportConfig.marginMm) {
                pdfDocument.addPage();
                currentY = exportConfig.marginMm;
            }

            pdfDocument.addImage(
                sectionImageData,
                'PNG',
                exportConfig.marginMm,
                currentY,
                contentWidth,
                sectionHeight,
                undefined,
                'FAST',
            );

            currentY += sectionHeight + sectionGap;
        };

        const { headerSections, dashboardCards } = getDashboardExportSections(
            dashboardElement,
            exportConfig.headerContainerSelector,
            exportConfig.cardRowSelector,
        );

        for (const headerSection of headerSections) {
            const headerCanvas = await captureSectionCanvas(
                html2canvas,
                headerSection,
                shouldIgnoreElement,
            );
            const headerImageData = headerCanvas.toDataURL('image/png');
            const headerHeight =
                (headerCanvas.height * contentWidth) / headerCanvas.width;

            renderFullWidthSection(
                headerImageData,
                headerHeight,
                exportConfig.headerSectionGapMm,
            );
        }

        const cardColumnGap = exportConfig.sectionGapMm;
        const columnsPerRow = Math.max(1, exportConfig.cardColumnsPerRow);
        const cardColumnWidth =
            (contentWidth - cardColumnGap * (columnsPerRow - 1)) /
            columnsPerRow;

        for (
            let index = 0;
            index < dashboardCards.length;
            index += columnsPerRow
        ) {
            const rowCards = dashboardCards.slice(index, index + columnsPerRow);
            const rowCardImages = [];

            for (const rowCardElement of rowCards) {
                const rowCardCanvas = await captureSectionCanvas(
                    html2canvas,
                    rowCardElement,
                    shouldIgnoreElement,
                );
                rowCardImages.push({
                    imageData: rowCardCanvas.toDataURL('image/png'),
                    height:
                        (rowCardCanvas.height * cardColumnWidth) /
                        rowCardCanvas.width,
                });
            }

            const rowHeight = Math.max(
                ...rowCardImages.map(card => card.height),
            );
            if (currentY + rowHeight > pageHeight - exportConfig.marginMm) {
                pdfDocument.addPage();
                currentY = exportConfig.marginMm;
            }

            rowCardImages.forEach((rowCard, rowCardIndex) => {
                const cardX =
                    exportConfig.marginMm +
                    rowCardIndex * (cardColumnWidth + cardColumnGap);
                pdfDocument.addImage(
                    rowCard.imageData,
                    'PNG',
                    cardX,
                    currentY,
                    cardColumnWidth,
                    rowCard.height,
                    undefined,
                    'FAST',
                );
            });

            currentY += rowHeight + exportConfig.sectionGapMm;
        }

        pdfDocument.save(exportConfig.filenameBuilder());
    } finally {
        restoreExportLayout();
    }
}

/**
 * Mock Population Group data to be used when data is not available in environments other than staging or prod, used for visually displaying data on the Population Groups dropdown.
 */
export const DASHBOARD_MOCK_POPULATION_GROUPS = [
    // The DashboardPage always defaults to "All Individuals" Population Group - and anytime the user adds different Population Groups, we remove the "All Individuals" selection.
    {
        id: 1,
        name: 'All Individuals',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.93Z',
    },
    {
        id: 290,
        name: 'Brads Population',
        isSystemGenerated: false,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-08-02T05:15:53.1988217Z',
    },
    {
        id: 52,
        name: 'Category-COBRA',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:23:53.94Z',
    },
    {
        id: 46,
        name: 'Category-Employee',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:23:53.94Z',
    },
    {
        id: 48,
        name: 'Category-Per Diem',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:23:53.94Z',
    },
    {
        id: 51,
        name: 'Category-Retiree',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:23:53.94Z',
    },
    {
        id: 50,
        name: 'Category-Temp',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:23:53.94Z',
    },
    {
        id: 33,
        name: 'Pay Group-Pay Group 1',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:22:14.9266667Z',
    },
    {
        id: 29,
        name: 'PlanType-401K',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.97Z',
    },
    {
        id: 30,
        name: 'PlanType-401K Roth',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.97Z',
    },
    {
        id: 7,
        name: 'PlanType-Dental',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.97Z',
    },
    {
        id: 10,
        name: 'PlanType-Dependent Care FSA',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.97Z',
    },
    {
        id: 45,
        name: 'PlanType-EAP',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-24T19:38:00.2766667Z',
    },
    {
        id: 9,
        name: 'PlanType-Healthcare FSA',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.97Z',
    },
    {
        id: 21,
        name: 'PlanType-Hospital Indemnity',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.97Z',
    },
    {
        id: 14,
        name: 'PlanType-LTD',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.97Z',
    },
    {
        id: 6,
        name: 'PlanType-Medical',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2024-10-04T14:57:53.97Z',
    },
    {
        id: 2,
        name: 'Status-Active',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:23:12.9933333Z',
    },
    {
        id: 5,
        name: 'Status-Inactive',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: 'Fahl, Andy',
        lastEdited: '2025-01-27T16:23:12.9933333Z',
    },
    {
        id: 4,
        name: 'Status-Leave of Absence',
        isSystemGenerated: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:23:12.9933333Z',
    },
    {
        id: 3,
        name: 'Status-Terminated',
        isSystemGenerated: true,
        isActive: true,
        lastEditedBy: null,
        lastEdited: '2025-01-27T16:23:12.9933333Z',
    },
];
